import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  FileText,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import { initials } from "@clinic/ui";
import { ROLE_LABELS, isClinician, type AuthUser } from "@clinic/types";
import { api } from "@/lib/api";
import { LogoFull, LogoIcon } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "@/lib/utils";

const COLLAPSE_KEY = "otzem-sidebar-collapsed";

type BadgeKey = "chat" | "mail";

const navItems: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
  doctorOnly?: boolean;
  badge?: BadgeKey;
}[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/insights", label: "Insights", icon: Sparkles, doctorOnly: true },
  { to: "/patients", label: "Patients", icon: Users },
  { to: "/appointments", label: "Appointments", icon: Calendar },
  { to: "/chat", label: "Chat", icon: MessageSquare, badge: "chat" },
  { to: "/mail", label: "Mail", icon: Mail, badge: "mail" },
  { to: "/prescriptions", label: "Prescriptions", icon: FileText },
  { to: "/audit", label: "Audit log", icon: ClipboardList, doctorOnly: true },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function Sidebar({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Poll unread counts so the badges stay live without a websocket.
  const chatUnread = useQuery({
    queryKey: ["chat", "unread"],
    queryFn: () => api.chat.unreadCount(),
    refetchInterval: 5000,
    staleTime: 2000,
  });
  const mailUnread = useQuery({
    queryKey: ["mail", "unread"],
    queryFn: () => api.mail.unreadCount(),
    refetchInterval: 5000,
    staleTime: 2000,
  });
  const unread: Record<BadgeKey, number> = {
    chat: chatUnread.data?.count ?? 0,
    mail: mailUnread.data?.count ?? 0,
  };

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  return (
    <aside
      className={cn(
        "flex h-screen shrink-0 flex-col border-r bg-bg-secondary transition-all duration-150",
        collapsed ? "w-16" : "w-60",
      )}
      style={{ borderColor: "var(--accent-subtle)" }}
    >
      {/* logo */}
      <div className={cn("flex items-center py-5", collapsed ? "justify-center" : "px-4")}>
        {collapsed ? <LogoIcon size={34} /> : <LogoFull compact iconSize={40} />}
      </div>

      {/* nav */}
      <nav className="flex-1 space-y-1 px-2 pt-2">
        {navItems
          .filter((item) => !item.doctorOnly || isClinician(user.role))
          .map((item) => {
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            const count = item.badge ? unread[item.badge] : 0;
            return (
              <Link
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={cn(
                  "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition",
                  collapsed && "justify-center px-0",
                  active
                    ? "bg-accent text-on-accent"
                    : "text-text-secondary hover:bg-bg-tertiary hover:text-text",
                )}
              >
                <item.icon size={18} className="shrink-0" />
                {!collapsed && <span>{item.label}</span>}
                {count > 0 &&
                  (collapsed ? (
                    <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-accent-light ring-2 ring-bg-secondary" />
                  ) : (
                    <span
                      className={cn(
                        "ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-pill px-1.5 text-xs font-semibold",
                        active ? "bg-on-accent/20 text-on-accent" : "bg-accent text-on-accent",
                      )}
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  ))}
              </Link>
            );
          })}
      </nav>

      {/* user box */}
      <div className="border-t p-3" style={{ borderColor: "var(--accent-subtle)" }}>
        <div className={cn("flex items-center gap-3", collapsed && "flex-col gap-2")}>
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-accent-subtle text-sm font-semibold text-accent-light"
            title={user.name}
          >
            {initials(user.name)}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user.name}</div>
              <div className="truncate text-xs text-text-tertiary">{ROLE_LABELS[user.role]}</div>
            </div>
          )}
          <div className={cn("flex gap-1", collapsed && "flex-col")}>
            <ThemeToggle />
            <button
              type="button"
              onClick={onLogout}
              title="Log out"
              className="flex h-9 w-9 items-center justify-center rounded-lg border bg-transparent text-text-secondary transition hover:bg-bg-tertiary hover:text-danger"
              style={{ borderColor: "var(--accent-subtle)" }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg py-1.5 text-xs text-text-tertiary transition hover:bg-bg-tertiary hover:text-text"
        >
          {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          {!collapsed && "Collapse"}
        </button>
      </div>
    </aside>
  );
}
